import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import type {
  DashboardDocumentListItem,
  DashboardDocumentMetric,
  DashboardDocumentSection,
  DashboardSummaryResponse,
  DocumentStatus,
} from '../../models/document.models';
import { EDITABLE_DOCUMENT_STATUSES } from '../../models/document.models';
import { DocumentApiService } from '../../services/document-api.service';
import { UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

type DashboardSectionKey =
  | 'waitingForMe'
  | 'waitingForOthers'
  | 'needsAttention'
  | 'recentlyCompleted';

interface DashboardSectionConfig {
  key: DashboardSectionKey;
  title: string;
  theme: 'me' | 'others' | 'attention' | 'completed';
  statusQueryParam?: DocumentStatus;
  emptyTitle: string;
  emptyMessage: string;
}

export interface LiveActivityItem {
  id: string;
  title: string;
  action: 'signed' | 'viewed' | 'sent' | 'created';
  actor: string;
  timeLabel: string;
  timestamp: number;
}

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, RouterLink],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  loading = true;
  errorMessage = '';
  summary: DashboardSummaryResponse | null = null;
  activeTab: DashboardSectionKey = 'waitingForMe';

  setActiveTab(key: DashboardSectionKey): void {
    this.activeTab = key;
  }

  get activeSection(): DashboardSectionConfig {
    return this.sections.find(s => s.key === this.activeTab)!;
  }


  getCurrentMonthLabel(): string {
    return new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
  }

  getWorkplaceHealthScore(): number {
    const total = this.summary?.metrics?.totalDocuments?.total ?? 0;
    const needsAttention = this.summary?.needsAttention?.count ?? 0;
    if (total === 0) return 100;
    return Math.max(0, Math.min(100, Math.round(((total - needsAttention) / total) * 100)));
  }

  timeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  }

  getLiveActivities(): LiveActivityItem[] {
    if (!this.summary) return [];
    const list: LiveActivityItem[] = [];

    // Map waitingForMe
    this.summary.waitingForMe.items.forEach(item => {
      list.push({
        id: item.id,
        title: item.title,
        action: 'viewed',
        actor: item.counterpartyName || item.counterpartyEmail || 'Recipient',
        timeLabel: this.timeAgo(item.updatedAt),
        timestamp: new Date(item.updatedAt).getTime()
      });
    });

    // Map waitingForOthers
    this.summary.waitingForOthers.items.forEach(item => {
      list.push({
        id: item.id,
        title: item.title,
        action: 'sent',
        actor: 'You',
        timeLabel: this.timeAgo(item.updatedAt),
        timestamp: new Date(item.updatedAt).getTime()
      });
    });

    // Map needsAttention
    this.summary.needsAttention.items.forEach(item => {
      list.push({
        id: item.id,
        title: item.title,
        action: 'created',
        actor: 'You',
        timeLabel: this.timeAgo(item.updatedAt),
        timestamp: new Date(item.updatedAt).getTime()
      });
    });

    // Map recentlyCompleted
    this.summary.recentlyCompleted.items.forEach(item => {
      const dateStr = item.completedAt || item.updatedAt;
      list.push({
        id: item.id,
        title: item.title,
        action: 'signed',
        actor: item.counterpartyName || item.counterpartyEmail || 'Recipient',
        timeLabel: this.timeAgo(dateStr),
        timestamp: new Date(dateStr).getTime()
      });
    });

    // Sort by timestamp desc, limit to 6
    return list.sort((a, b) => b.timestamp - a.timestamp).slice(0, 6);
  }


  readonly sections: DashboardSectionConfig[] = [
    {
      key: 'waitingForMe',
      title: 'Waiting for me',
      theme: 'me',
      emptyTitle: 'No documents awaiting for you',
      emptyMessage: 'Everything assigned to you is cleared for now.',
    },
    {
      key: 'waitingForOthers',
      title: 'Waiting for others',
      theme: 'others',
      statusQueryParam: 'waiting_for_others',
      emptyTitle: 'No documents are waiting on recipients',
      emptyMessage: 'Documents that are out for signature will appear here.',
    },
    {
      key: 'needsAttention',
      title: 'Needs attention',
      theme: 'attention',
      statusQueryParam: 'needs_attention',
      emptyTitle: 'No documents need attention',
      emptyMessage: 'Delivery failures and follow-up items will show here.',
    },
    {
      key: 'recentlyCompleted',
      title: 'Recently completed',
      theme: 'completed',
      statusQueryParam: 'completed',
      emptyTitle: 'No completed documents yet',
      emptyMessage: 'Completed documents will appear here once recipients finish signing.',
    },
  ];

  private readonly api = inject(DocumentApiService);
  private readonly router = inject(Router);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadSummary();
  }

  loadSummary(): void {
    this.loading = true;
    this.errorMessage = '';

    this.api
      .getDashboardSummary()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (summary) => {
          this.summary = summary;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.summary = null;
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Failed to load dashboard',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  sectionData(key: DashboardSectionKey): DashboardDocumentSection {
    return this.summary?.[key] ?? { count: 0, items: [] };
  }

  metricCard(key: 'totalDocuments' | 'thisMonthDocuments'): DashboardDocumentMetric {
    return this.summary?.metrics[key] ?? { total: 0, sent: 0, received: 0 };
  }

  viewAllQueryParams(section: DashboardSectionConfig): { status?: DocumentStatus } {
    return section.statusQueryParam ? { status: section.statusQueryParam } : {};
  }

  handleSectionItemClick(section: DashboardSectionConfig, item: DashboardDocumentListItem): void {
    if (section.key === 'needsAttention' && EDITABLE_DOCUMENT_STATUSES.includes(item.status)) {
      void this.router.navigate(['/documents', item.id, 'edit']);
      return;
    }

    void this.router.navigate(['/documents', item.id, 'overview'], {
      queryParams: section.statusQueryParam ? { status: section.statusQueryParam } : {},
    });
  }

  subtitleFor(section: DashboardSectionConfig, item: DashboardDocumentListItem): string {
    switch (section.key) {
      case 'needsAttention':
        return item.statusReason?.trim() || 'Action required to continue this document';
      case 'recentlyCompleted':
        return item.counterpartyName
          ? `Completed by ${item.counterpartyName}`
          : item.counterpartyEmail
            ? `Completed by ${item.counterpartyEmail}`
            : 'Completed document';
      default:
        return item.counterpartyName
          ? `To: ${item.counterpartyName}`
          : item.counterpartyEmail
            ? `To: ${item.counterpartyEmail}`
            : 'Awaiting recipient activity';
    }
  }

  timestampLabelFor(section: DashboardSectionConfig, item: DashboardDocumentListItem): string {
    if (section.key === 'recentlyCompleted' && item.completedAt) {
      return this.dateTimeFormat.formatDateTime(item.completedAt);
    }

    if (section.key === 'waitingForOthers' && item.scheduledFor) {
      return `Scheduled ${this.dateTimeFormat.formatDateTime(item.scheduledFor)}`;
    }

    return this.dateTimeFormat.formatDateTime(item.updatedAt);
  }
}
